import React, { useCallback, useEffect, useState } from "react";
import Layout from "../../layout/Layout";
import {
  Form,
  InputGroup,
  OverlayTrigger,
  Table,
  Tooltip,
} from "react-bootstrap";
import CustomPagination from "../../components/CustomPagination";
import DeleteModal from "../../components/DeleteModal";
import EditBroadcastNotification from "../../components/broadcast/EditBroadcastNotification";
import ViewBroadcastNotification from "../../components/broadcast/ViewBroadcastNotification";
import { useGlobalState } from "../../context/GlobalStateContext";
import axiosInstance from "../../utils/axiosConfig";
import NoDateFound from "../../components/NoDateFound";
import Loader from "../../components/loader/Loader";
import * as CustomToast from "../../components/CustomToastMessage";

function BroadcastNotifications() {
  const [broadcastNotifications, setBroadcastNotifications] = useState([]);
  const {
    loading,
    setLoading,
    search,
    pagination,
    setPagination,
    handleSearch,
    handleLimitChange,
  } = useGlobalState();

  const [show, setShow] = useState(false);
  const [selectedBroadcastId, setSelectedBroadcastId] = useState(null);
  const [optionsData, setOptionsData] = useState({
    userList: {
      data: [],
      pagination: {},
    },
    blogList: {
      data: [],
      pagination: {},
    },
  });
  const [pageState, setPageState] = useState({
    user: 1,
    blog: 1,
  });
  const [optionLoading, setOptionLoading] = useState({
    userList: false,
    blogList: false,
  });

  const handleClose = () => {
    setShow(false);
    setSelectedBroadcastId(null);
  };
  const handleShow = (id) => {
    setSelectedBroadcastId(id);
    setShow(true);
  };

  const fetchBroadcasts = useCallback(
    async (page = 1, limit = 10, searchQuery = "") => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/broadcast/list`,
          {
            params: {
              page,
              limit,
              search: searchQuery,
            },
          }
        );
        if (response.data.success) {
          setBroadcastNotifications(response.data.data);
          setPagination(response.data.pagination);
        } else {
          setBroadcastNotifications([]);
          setPagination((prev) => ({
            ...prev,
            currentPage: 1,
            totalPages: 1,
            totalItems: 0,
          }));
        }
      } catch (error) {
        if (error.response.data.status === 400) {
          CustomToast.error(error.response?.data?.message);
        }
        console.error(error.response?.data?.message || "Internal server error");
        setBroadcastNotifications([]);
        setPagination((prev) => ({
          ...prev,
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
        }));
      } finally {
        setLoading(false);
      }
    },
    [setLoading, setPagination]
  );

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      const response = await axiosInstance.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/broadcast/${id}`
      );
      if (response.data.success) {
        CustomToast.success(response.data.message);
        fetchBroadcasts(pagination.currentPage, pagination.limit, search);
      }
    } catch (error) {
      if (error.response.data.status === 400) {
        CustomToast.error(error.response?.data?.message);
      }
      console.error(error.response?.data?.message || "Internal server error");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchBroadcasts(pagination.currentPage, pagination.limit, search);
  }, [pagination.currentPage, pagination.limit, search, fetchBroadcasts]);

  const fetchUsers = async (data = {}) => {
    const { page = 1, limit = 10, searchQuery = "" } = data;
    setOptionLoading((prev) => ({ ...prev, userList: true }));
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/list`,
        {
          params: {
            // page,
            // limit,
            // search: searchQuery,
            paginationOptional: true,
          },
        }
      );
      if (response.data.success) {
        setOptionsData((prev) => ({
          ...prev,
          userList: {
            data:
              page === 1
                ? response.data.data
                : [...prev.userList.data, ...response.data.data],
            pagination: response.data.pagination,
          },
        }));
      } else {
        setOptionsData((prev) => ({
          ...prev,
          userList: {
            data: [],
            pagination: {},
          },
        }));
      }
    } catch (error) {
      setOptionsData((prev) => ({
        ...prev,
        userList: {
          data: [],
          pagination: {},
        },
      }));
      if (error.response?.data?.status === 400) {
        CustomToast.error(error.response?.data?.message);
      }
      console.error(error.response?.data?.message || "Internal server error");
    } finally {
      setOptionLoading((prev) => ({ ...prev, userList: false }));
    }
  };

  const fetchBlogs = async (data = {}) => {
    const { page = 1, limit = 10, searchQuery = "" } = data;
    setOptionLoading((prev) => ({ ...prev, blogList: true }));
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/blogs/admin/list`,
        {
          params: {
            // page,
            // limit,
            // search: searchQuery,
            paginationOptional: true,
          },
        }
      );
      if (response.data.success) {
        setOptionsData((prev) => ({
          ...prev,
          blogList: {
            data:
              page === 1
                ? response.data.data
                : [...prev.blogList.data, ...response.data.data],
            pagination: response.data.pagination,
          },
        }));
      } else {
        setOptionsData((prev) => ({
          ...prev,
          blogList: {
            data: [],
            pagination: {},
          },
        }));
      }
    } catch (error) {
      setOptionsData((prev) => ({
        ...prev,
        blogList: {
          data: [],
          pagination: {},
        },
      }));
      console.error(error.response?.data?.message || "Error fetching blogs");
    } finally {
      setOptionLoading((prev) => ({ ...prev, blogList: false }));
    }
  };

  useEffect(() => {
    const fetchOptionsData = async () => {
      await fetchUsers();
      await fetchBlogs();
    };
    fetchOptionsData();
  }, []);

  return (
    <Layout title="Broadcast Notifications List">
      <main>
        <div className="card page-card">
          <div className="card-header d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
            <div className="d-flex align-items-center gap-3">
              <p>Show</p>
              <Form.Select
                aria-label="Default select example"
                value={pagination.limit}
                onChange={handleLimitChange}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
              </Form.Select>
              <p>entries</p>
            </div>

            <div className="d-flex flex-column flex-md-row  align-items-start align-items-md-center gap-3">
              <div className="header-search">
                <InputGroup>
                  <InputGroup.Text id="basic-addon1" className="bg-transparent">
                    <img src="/images/png/search.png" width={20} alt="" />
                  </InputGroup.Text>
                  <Form.Control
                    type="search"
                    className="border-start-0 ps-0"
                    placeholder="search here..."
                    value={search}
                    onChange={handleSearch(fetchBroadcasts)}
                  />
                </InputGroup>
              </div>
              {/* <AddOption /> */}

              <button
                className="btn-primary fs-16 fw-600 d-flex justify-content-center align-items-center gap-2 px-3"
                onClick={() => handleShow(null)}
              >
                <img src="/images/png/plus.png" width={22} alt="add" />
                Add New
              </button>
            </div>
          </div>
          <div className="card-body">
            <div className="overflow-auto">
              <Table className="vertical-align">
                <thead>
                  <tr>
                    <th>Id</th>
                    <th>Title</th>
                    <th>Schedule Time</th>
                    <th>Created Date</th>
                    <th>Send</th>
                    <th width="100" className="text-center">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="text-center">
                        <Loader />
                      </td>
                    </tr>
                  ) : broadcastNotifications.length > 0 ? (
                    broadcastNotifications.map((item, index) => (
                      <tr key={item._id}>
                        <td>
                          <p className="mb-0">
                            {(pagination.currentPage - 1) * pagination.limit +
                              index +
                              1}
                          </p>
                        </td>
                        <td>{item.title ? item.title : "-"}</td>
                        <td>
                          {item.scheduleTime
                            ? new Date(item.scheduleTime)
                                .toLocaleString()
                                .split("T")[0]
                            : "-"}
                        </td>
                        <td>
                          {item.createdAt
                            ? new Date(item.createdAt)
                                .toLocaleString()
                                .split("T")[0]
                            : "-"}
                        </td>
                        <td>{item.isSend === 1 ? "Yes" : "No"}</td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <ViewBroadcastNotification broadcast={item} userLength={optionsData.userList.data?.length} />
                            <OverlayTrigger
                              key="top"
                              placement="top"
                              overlay={
                                <Tooltip id="tooltip-top">
                                  {item.isSend === 1
                                    ? "Cannot edit sent broadcast"
                                    : "Edit"}
                                </Tooltip>
                              }
                            >
                              <div
                                className={`${
                                  item.isSend === 1
                                    ? "opacity-50 pe-none"
                                    : "pointer"
                                }`}
                              >
                                <img
                                  src="/images/png/edit.png"
                                  onClick={() => handleShow(item._id)}
                                  width={35}
                                  alt="action"
                                />
                              </div>
                            </OverlayTrigger>
                            <DeleteModal
                              onConfirm={() => handleDelete(item._id)}
                              loading={loading}
                              flag={"broadcast"}
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="10" className="text-center">
                        <NoDateFound />
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
            <CustomPagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              totalItems={pagination.totalItems}
              limit={pagination.limit}
              onPageChange={(page) =>
                setPagination((prev) => ({ ...prev, currentPage: page }))
              }
            />
          </div>
        </div>
      </main>
      <EditBroadcastNotification
        show={show}
        handleClose={handleClose}
        onOptionAdded={() => fetchBroadcasts(1, pagination.limit, search)}
        id={selectedBroadcastId}
        optionsData={optionsData}
      />
    </Layout>
  );
}

export default BroadcastNotifications;
